import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box } from '@mui/material';
import { useRuleSetDetail } from '../../hooks/useRuleSetDetail';
import { usePageBreadcrumbs } from '../../hooks/usePageBreadcrumbs';
import RuleSetHeader from './RuleSetHeader';
import FieldConfigurationSection from './sections/FieldConfigurationSection';
import AutoExportSettingsSection from './sections/AutoExportSettingsSection';
import GlobalPromptContextSection from './sections/GlobalPromptContextSection';
import LoadingSpinner from '../shared/LoadingSpinner';
import ErrorAlert from '../shared/ErrorAlert';

/**
 * RuleSetDetailView - Display rule set-specific configuration (Field Configuration)
 * 
 * Shows rule set information and field configuration section
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5
 */
const RuleSetDetailView = () => {
  const { customerId, rulesetId } = useParams();
  const navigate = useNavigate();
  const { ruleset, customer, loading, error } = useRuleSetDetail(customerId, rulesetId);

  const customerLabel = customer?.customerName || 'Loading...';
  const rulesetLabel = ruleset?.name || 'Loading...';
  usePageBreadcrumbs([
    { label: 'Customers', path: '/customers' },
    { label: customerLabel, path: `/customers/${customerId}` },
    { label: rulesetLabel }
  ], [customerLabel, rulesetLabel]);

  /**
   * Handle 404 errors - redirect to customer detail
   */
  useEffect(() => {
    if (error && (error.includes('not found') || error.includes('does not belong'))) {
      // Redirect to customer detail after a brief delay
      const timer = setTimeout(() => {
        navigate(`/customers/${customerId}`);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [error, navigate, customerId]);

  /**
   * Handle back navigation to customer detail
   */
  const handleBack = () => {
    navigate(`/customers/${customerId}`);
  };

  /**
   * Handle error alert close
   */
  const handleErrorClose = () => {
    // Error will be cleared on next successful fetch
  };

  // Show loading spinner while fetching
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <LoadingSpinner size="large" />
      </Container>
    );
  }

  // Show error alert if error occurred
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <ErrorAlert 
          message={error} 
          onClose={handleErrorClose}
        />
        {(error.includes('not found') || error.includes('does not belong')) && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            Redirecting to customer detail...
          </Box>
        )}
      </Container>
    );
  }

  // Don't render if no rule set or customer data
  if (!ruleset || !customer) {
    return null;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Rule Set Header */}
      <RuleSetHeader 
        ruleset={ruleset} 
        onBack={handleBack}
      />

      {/* Auto-Export Settings Section */}
      <AutoExportSettingsSection
        rulesetId={rulesetId}
      />

      {/* Global Prompt Context Section */}
      <GlobalPromptContextSection
        rulesetId={rulesetId}
      />

      {/* Field Configuration Section */}
      <FieldConfigurationSection
        customerId={customerId}
        rulesetId={rulesetId}
      />
    </Container>
  );
};

export default RuleSetDetailView;
